import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Alert, Button, FieldError, FieldRow, Input, Label } from '@sentinel/ui';
import { recoveryCodeSchema, type RecoveryCodeInput } from '../schema';

export interface RecoveryChallengeStepProps {
  onSubmit: (recoveryCode: string) => Promise<void> | void;
  /** Inline error such as "verify_invalid_recovery_code". */
  error?: string | null;
  submitting?: boolean;
  /**
   * If set, render a "Lost your device?" link below the form pointing at
   * the dedicated `/mfa/recovery` route. The href should already carry the
   * mfa_token (and optional return_to) so the user lands on the
   * re-enrollment-focused flow without re-entering credentials.
   */
  lostDeviceHref?: string;
}

export function RecoveryChallengeStep({
  onSubmit,
  error,
  submitting,
  lostDeviceHref,
}: RecoveryChallengeStepProps) {
  const { t } = useTranslation('mfa');
  const form = useForm<RecoveryCodeInput>({ resolver: zodResolver(recoveryCodeSchema) });

  async function handleSubmit(values: RecoveryCodeInput) {
    await onSubmit(values.recovery_code.trim());
  }

  const codeErrorRaw = form.formState.errors.recovery_code?.message;
  const codeError =
    codeErrorRaw === 'recovery_code_invalid' ? t('verify_invalid_recovery_code') : codeErrorRaw;

  return (
    <div
      role="tabpanel"
      id="mfa-challenge-panel-recovery"
      aria-labelledby="mfa-challenge-tab-recovery"
      style={{ display: 'grid', gap: '1rem' }}
    >
      {error ? (
        <Alert type="error" role="alert">
          {error}
        </Alert>
      ) : null}

      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        style={{ display: 'grid', gap: '0.75rem' }}
      >
        <FieldRow>
          <Label htmlFor="mfa-verify-recovery-code">{t('recovery_code_label')}</Label>
          <Input
            id="mfa-verify-recovery-code"
            data-testid="mfa-verify-recovery-code"
            type="text"
            autoComplete="one-time-code"
            spellCheck={false}
            autoCapitalize="none"
            autoFocus
            aria-invalid={form.formState.errors.recovery_code ? 'true' : undefined}
            {...form.register('recovery_code')}
          />
          <FieldError message={codeError} />
        </FieldRow>
        <Button
          type="submit"
          data-testid="mfa-verify-recovery-submit"
          disabled={Boolean(submitting) || form.formState.isSubmitting}
        >
          {t('recovery_submit')}
        </Button>
      </form>

      {lostDeviceHref ? (
        <p style={{ margin: 0, fontSize: '0.9rem' }}>
          <a href={lostDeviceHref} data-testid="mfa-verify-lost-device">
            {t('recovery_lost_device_link')}
          </a>
        </p>
      ) : null}
    </div>
  );
}
